import { createContext, useContext } from 'react';
import { fromAbsolute, getLocalTimeZone, type ZonedDateTime } from '@internationalized/date';
import { DatePicker, type DatePickerValue, Select, Text, TextField, Tooltip } from '@customerio/pluma-components/react';
import plur from 'plur';
import { isEmptyString } from '@/utils/string';
import { timestampOperatorForVariant, type TimestampVariant } from '../../operators';
import {
  TIME_DIRECTION_AGO,
  TIME_DIRECTION_FROM_NOW,
  isAbsoluteTimestampOperator,
  relativeTimestampDays,
  relativeTimestampDirection,
  relativeTimestampForTypedDays,
  relativeTimestampWithDirection,
} from '../../relative-timestamp';
import { valueForOperatorChange } from './value-for-operator-change';
import type { ConditionControlSize, OperatorValuePatch } from './types';

/**
 * What the conditions find, as the relative date's explanation names them:
 * "items" unless the surface says otherwise, such as "objects" on a step
 * that queries objects. Set through `OperatorValue`'s `subjectNoun`.
 */
export const SubjectNounContext = createContext('items');

function timestampVariant(operator: string): TimestampVariant {
  return isAbsoluteTimestampOperator(operator) ? 'absolute' : 'relative';
}

function epochToDate(value: string | null): ZonedDateTime | null {
  if (isEmptyString(value)) {
    return null;
  }
  const seconds = Number.parseInt(value ?? '', 10);
  return Number.isNaN(seconds) ? null : fromAbsolute(seconds * 1000, getLocalTimeZone());
}

function dateToEpoch(value: DatePickerValue | null): string | null {
  if (value == null || 'start' in value) {
    return null;
  }
  const date = 'timeZone' in value ? value.toDate() : value.toDate(getLocalTimeZone());
  return String(Math.floor(date.getTime() / 1000));
}

// Computed from the stored seconds, and with a time: the day field rounds, so
// this is the only place a reader can see an offset finer than a day.
function relativeTimestampOutput(value: string | null): string {
  return new Date(Date.now() + Number(value ?? '0') * 1000).toLocaleString();
}

export type TimestampVariantSelectProps = {
  disabled?: boolean;
  operator: string;
  value: string | null;
  onChange: (patch: OperatorValuePatch) => void;
  size?: ConditionControlSize;
};

/** Swaps a timestamp inequality between its relative and absolute spellings. */
export function TimestampVariantSelect({
  disabled,
  operator,
  value,
  onChange,
  size = 'sm',
}: TimestampVariantSelectProps) {
  const handleTimestampVariantChange = (variant: string | null) => {
    if (variant !== 'relative' && variant !== 'absolute') {
      return;
    }
    const nextOperator = timestampOperatorForVariant(operator, variant);
    onChange({ operator: nextOperator, value: valueForOperatorChange(nextOperator, operator, value) });
  };

  return (
    <Select
      ariaLabel="Timestamp variant"
      size={size}
      options={[
        { value: 'relative', label: 'A relative date' },
        { value: 'absolute', label: 'A specific date' },
      ]}
      value={timestampVariant(operator)}
      onChange={handleTimestampVariantChange}
      isDisabled={disabled}
    />
  );
}

export type RelativeTimestampValueProps = {
  disabled?: boolean;
  field: string;
  operator: string;
  value: string | null;
  error?: string;
  onChange: (patch: OperatorValuePatch) => void;
  size?: ConditionControlSize;
};

/** A signed offset, edited as a whole day count plus a direction. */
export function RelativeTimestampValue({
  disabled,
  field,
  operator,
  value,
  error,
  onChange,
  size = 'sm',
}: RelativeTimestampValueProps) {
  const days = relativeTimestampDays(value);
  const direction = relativeTimestampDirection(value);
  const subjectNoun = useContext(SubjectNounContext);

  return (
    <>
      <TextField
        ariaLabel="Timestamp"
        type="number"
        min="0"
        size={size}
        placeholder="1"
        value={String(days)}
        style={{ width: '75px' }}
        error={error}
        onInput={(event) => {
          if (event.target instanceof HTMLInputElement) {
            onChange({ value: relativeTimestampForTypedDays(event.target.value, value) });
          }
        }}
        isDisabled={disabled}
      />
      <Text as="span" color="secondary">
        {plur('day', days)}
      </Text>
      <Select
        ariaLabel="Time direction"
        size={size}
        options={[
          { value: TIME_DIRECTION_AGO, label: 'Ago' },
          { value: TIME_DIRECTION_FROM_NOW, label: 'From now' },
        ]}
        value={direction}
        onChange={(nextDirection) => {
          if (nextDirection != null) {
            onChange({ value: relativeTimestampWithDirection(value, nextDirection) });
          }
        }}
        // At zero the two directions describe the same instant, so the control
        // has nothing to choose between.
        isDisabled={disabled === true || Number(value ?? '0') === 0}
      />
      <Tooltip
        placement="bottom"
        content={`If run right now, this condition would find ${subjectNoun} with ${field} date on or ${
          operator === 'timestamp_lt' ? 'before' : 'after'
        } ${relativeTimestampOutput(value)}.`}
      >
        <Text as="span" color="secondary" style={{ textDecoration: 'underline dotted' }}>
          What's the output?
        </Text>
      </Tooltip>
    </>
  );
}

export type AbsoluteTimestampValueProps = {
  disabled?: boolean;
  value: string | null;
  error?: string;
  onChange: (patch: OperatorValuePatch) => void;
  size?: ConditionControlSize;
};

/** A single epoch, edited as a date and time. */
export function AbsoluteTimestampValue({ disabled, value, error, onChange, size = 'sm' }: AbsoluteTimestampValueProps) {
  return (
    <DatePicker
      ariaLabel="Date and time"
      withTime={true}
      placeholder="Date and time"
      size={size}
      value={epochToDate(value)}
      error={error}
      onChange={(nextDate) => onChange({ value: dateToEpoch(nextDate) })}
      isDisabled={disabled}
    />
  );
}

export type TimestampRangeValueProps = {
  disabled?: boolean;
  value: string | null;
  value2: string | null;
  error?: string;
  error2?: string;
  onChange: (patch: OperatorValuePatch) => void;
  size?: ConditionControlSize;
};

/** The epoch pair a `timestamp_bw` condition holds. */
export function TimestampRangeValue({
  disabled,
  value,
  value2,
  error,
  error2,
  onChange,
  size = 'sm',
}: TimestampRangeValueProps) {
  return (
    <>
      <DatePicker
        ariaLabel="Start date and time"
        withTime={true}
        placeholder="Date and time"
        size={size}
        value={epochToDate(value)}
        error={error}
        onChange={(nextDate) => onChange({ value: dateToEpoch(nextDate) })}
        isDisabled={disabled}
      />
      <Text as="span" color="secondary">
        and
      </Text>
      <DatePicker
        ariaLabel="End date and time"
        withTime={true}
        placeholder="Date and time"
        size={size}
        value={epochToDate(value2)}
        error={error2}
        onChange={(nextDate) => onChange({ value2: dateToEpoch(nextDate) })}
        isDisabled={disabled}
      />
    </>
  );
}
